'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { mutate as globalMutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton';
import LoadMoreButton from '@/components/LoadMoreButton';
import { useAppLoaderContext } from '@/context/AppLoaderContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useDigitalAssetPods } from '@/hooks/useDigitalAssetPods';
import { useRoomPolicy } from '@/hooks/useRoomPolicy';
import { getCardType } from '@/lib/viverse-world-card';
import { ContentTypeCode, CONTENT_TYPE_CODE_TO_ID, getMyRoomsV3, type MyRoomsContentTypeId, type Room } from '@/services/cms';
const PAGE_SIZE = 8;
type PageData = {
  rooms: Room[];
  total: number;
};
type ContentSectionConfig = {
  contentTypeCode: ContentTypeCode;
  contentTypeId: MyRoomsContentTypeId;
  titleKey: string;
};
export const PRIVATE_CONTENT_SECTIONS: readonly ContentSectionConfig[] = [{
  contentTypeCode: 'games',
  contentTypeId: CONTENT_TYPE_CODE_TO_ID.games,
  titleKey: 'profile-page.games.title'
}, {
  contentTypeCode: 'experiences',
  contentTypeId: CONTENT_TYPE_CODE_TO_ID.experiences,
  titleKey: 'profile-page.experiences.title'
}, {
  contentTypeCode: 'videos',
  contentTypeId: CONTENT_TYPE_CODE_TO_ID.videos,
  titleKey: 'profile-page.videos.title'
}];
type UserMyContentSectionProps = ContentSectionConfig & {
  stopSetAsFeatured: boolean;
};
export default function UserMyContentSection(t0) {
  const $ = _c(68);
  const {
    contentTypeCode,
    contentTypeId,
    titleKey,
    stopSetAsFeatured
  } = t0;
  const t = useTranslations();
  const roomPolicy = useRoomPolicy();
  const {
    store
  } = useConfigsContext();
  const {
    setIsAppLoading
  } = useAppLoaderContext();
  const {
    refreshDigitalAssetPods
  } = useDigitalAssetPods();
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  let t1;
  if ($[0] !== contentTypeCode) {
    t1 = (pageIndex, previousPageData) => {
      if (previousPageData && previousPageData.rooms.length === 0) {
        return null;
      }
      return `my-rooms:${contentTypeCode}:${pageIndex}`;
    };
    $[0] = contentTypeCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== contentTypeId || $[3] !== htcToken) {
    t2 = async key => {
      const pageIndex_0 = Number(key.split(":").pop());
      const res = await getMyRoomsV3(htcToken, {
        start: pageIndex_0 * PAGE_SIZE,
        limit: PAGE_SIZE,
        contentTypeId
      });
      if (res?.status === 200) {
        return {
          rooms: res.results.results,
          total: res.results.pages?.total ?? res.results.results.length
        };
      }
      return {
        rooms: [],
        total: 0
      };
    };
    $[2] = contentTypeId;
    $[3] = htcToken;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      revalidateFirstPage: false
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    isLoading,
    isValidating,
    mutate,
    size,
    setSize
  } = useSWRInfinite(t1, t2, t3);
  let enableLoadMore;
  let isFetching;
  let skeletonCount;
  let t10;
  let t11;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== cmsAccountId || $[7] !== contentTypeCode || $[8] !== data || $[9] !== isLoading || $[10] !== isValidating || $[11] !== mutate || $[12] !== refreshDigitalAssetPods || $[13] !== roomPolicy || $[14] !== setIsAppLoading || $[15] !== stopSetAsFeatured || $[16] !== t || $[17] !== titleKey) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rooms = data?.flatMap(_temp) ?? [];
      const total = data?.[0]?.total ?? 0;
      enableLoadMore = total > rooms.length;
      skeletonCount = data ? Math.min(PAGE_SIZE, total - rooms.length) : PAGE_SIZE;
      let t12;
      if ($[29] !== cmsAccountId || $[30] !== mutate || $[31] !== refreshDigitalAssetPods || $[32] !== setIsAppLoading) {
        t12 = async function handleAfterMutatingWorld() {
          refreshDigitalAssetPods?.();
          await Promise.all([mutate(), globalMutate(["featured-rooms", cmsAccountId])]);
          setIsAppLoading(false);
        };
        $[29] = cmsAccountId;
        $[30] = mutate;
        $[31] = refreshDigitalAssetPods;
        $[32] = setIsAppLoading;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      const handleAfterMutatingWorld = t12;
      const hasLoaded = !isLoading && data !== undefined;
      if (hasLoaded && rooms.length === 0) {
        t11 = null;
        break bb0;
      }
      isFetching = isLoading || isValidating;
      t8 = contentTypeCode;
      t9 = "relative flex scroll-mt-40 flex-col gap-6 md:gap-9";
      let t13;
      if ($[34] !== t || $[35] !== titleKey) {
        t13 = t(titleKey);
        $[34] = t;
        $[35] = titleKey;
        $[36] = t13;
      } else {
        t13 = $[36];
      }
      if ($[37] !== t13) {
        t10 = <h2 className="subheader-2 md:header-5 text-vive-primary-20 relative font-bold">{t13}</h2>;
        $[37] = t13;
        $[38] = t10;
      } else {
        t10 = $[38];
      }
      t7 = "flex flex-col gap-10";
      t6 = "no-scrollbar flex w-full flex-col max-lg:overflow-scroll max-lg:scroll-smooth";
      t4 = "flex gap-5 md:-mx-2 md:gap-2 lg:flex-wrap lg:gap-2";
      let t14;
      if ($[39] !== contentTypeCode || $[40] !== handleAfterMutatingWorld || $[41] !== roomPolicy || $[42] !== stopSetAsFeatured) {
        t14 = room => <div key={`my-${contentTypeCode}-${room.id}`}><ViverseWorldCard className="max-w-[306px] min-w-[306px] lg:max-w-[322px] lg:min-w-[322px]" info={room} showMoreAction={true} onAfterMutatingWorld={handleAfterMutatingWorld} customDesc={Object.values(roomPolicy).find(policy => policy.value === room.policy)?.title || room.policy} stopSetAsFeatured={stopSetAsFeatured} hidePremiumBadge={false} cardType={room.content_type ? getCardType(room.content_type) : contentTypeCode} /></div>;
        $[39] = contentTypeCode;
        $[40] = handleAfterMutatingWorld;
        $[41] = roomPolicy;
        $[42] = stopSetAsFeatured;
        $[43] = t14;
      } else {
        t14 = $[43];
      }
      t5 = rooms.map(t14);
    }
    $[6] = cmsAccountId;
    $[7] = contentTypeCode;
    $[8] = data;
    $[9] = isLoading;
    $[10] = isValidating;
    $[11] = mutate;
    $[12] = refreshDigitalAssetPods;
    $[13] = roomPolicy;
    $[14] = setIsAppLoading;
    $[15] = stopSetAsFeatured;
    $[16] = t;
    $[17] = titleKey;
    $[18] = enableLoadMore;
    $[19] = isFetching;
    $[20] = skeletonCount;
    $[21] = t10;
    $[22] = t11;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    enableLoadMore = $[18];
    isFetching = $[19];
    skeletonCount = $[20];
    t10 = $[21];
    t11 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[44] !== isFetching || $[45] !== skeletonCount) {
    t12 = isFetching && Array.from({
      length: skeletonCount
    }).map(_temp2);
    $[44] = isFetching;
    $[45] = skeletonCount;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t12 || $[48] !== t4 || $[49] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[47] = t12;
    $[48] = t4;
    $[49] = t5;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t13 || $[52] !== t6) {
    t14 = <div className={t6}>{t13}</div>;
    $[51] = t13;
    $[52] = t6;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== enableLoadMore || $[55] !== isFetching || $[56] !== setSize || $[57] !== size) {
    t15 = enableLoadMore && <div className="flex flex-1 justify-center"><LoadMoreButton isLoading={isFetching} onClick={() => setSize(size + 1)} /></div>;
    $[54] = enableLoadMore;
    $[55] = isFetching;
    $[56] = setSize;
    $[57] = size;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t14 || $[60] !== t15 || $[61] !== t7) {
    t16 = <div className={t7}>{t14}{t15}</div>;
    $[59] = t14;
    $[60] = t15;
    $[61] = t7;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] !== t10 || $[64] !== t16 || $[65] !== t8 || $[66] !== t9) {
    t17 = <section id={t8} className={t9}>{t10}{t16}</section>;
    $[63] = t10;
    $[64] = t16;
    $[65] = t8;
    $[66] = t9;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  return t17;
}
function _temp2(_, i) {
  return <ViverseWorldCardSkeleton key={`skeleton-${i}`} className="max-w-[306px] min-w-[306px] lg:max-w-[322px] lg:min-w-[322px]" />;
}
function _temp(page) {
  return page.rooms;
}